import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, ArrowLeft, Paperclip, X, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Loading } from "@/components/common/Loading";
import { GroupAssociationFields } from "@/components/groups/GroupAssociationFields";
import { CoverUploader, uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";
import { RichTextEditor, FULL_TOOLBAR } from "@/components/forms/RichTextEditor";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { LocationFields } from "@/components/forms/AddressFields";
import { useReferences } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";

const opts = (l) => (l || []).map((x) => ({ value: x.value, label: x.label }));

// Rich-text counter — strips HTML and decodes common entities.
const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;
const RefSelect = ({ list, value, onChange, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-64">{(list || []).map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
  </Select>
);

export default function ResourceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { references } = useReferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [o, setO] = useState(null);
  const [saving, setSaving] = useState(false);
  // Groups + visibility (GRP-DEC-315/316). Seeded from the object's own
  // associations and audience scope, which only the server writes.
  const [groupIds, setGroupIds] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [uploading, setUploading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const fileRef = useRef(null);

  // ONE place that seeds the Group controls, called only when the SERVER hands us the
  // object (load, or the Save reply) — see EventEditor for the defect this replaced.
  const hydrate = (doc) => {
    setO(doc);
    setGroupIds((doc.groups || [])
      .filter((l) => ["approved", "pending", "awaiting_owner"].includes(l.state))
      .map((l) => l.group_id));
    setVisibility(doc.scope === "group" ? "groups" : "public");
  };

  useEffect(() => {
    api.get(`/resources/${id}`).then((r) => {
      const d = r.data;
      // Migrate legacy single-file resources into the multi-file array so they show in the editor.
      if ((!d.files || !d.files.length) && d.file && d.file.url) d.files = [d.file];
      hydrate(d);
    }).catch(() => { toast.error("Not found"); navigate("/dashboard/resources"); });
    api.get("/orgs/business/mine").then((r) => setBusinesses(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!o) return <Loading />;
  const set = (k, v) => setO((s) => ({ ...s, [k]: v }));

  const save = async (extra = {}) => {
    if (PLAIN(o.description) > 1500) { toast.error("Description must be 1,500 characters or fewer."); return; }    setSaving(true);
    try {
      const body = { ...o, ...extra };
      ["id", "_id", "owner_id", "created_at", "status", "kind", "author", "is_owner", "my_rating", "rating_avg", "rating_count", "location_geo", "groups", "group_results", "scope"].forEach((k) => delete body[k]);
      body.group_ids = groupIds;
      body.visibility = visibility;
      const { data } = await api.put(`/resources/${id}`, body);
      hydrate(data);
      const failed = (data.group_results || []).filter((r) => r.error);
      if (failed.length) {
        toast.error(`Saved, but ${failed.length} group couldn't be added: ${failed[0].error}`);
      } else {
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if ((o.files || []).length >= 3) { toast.error("You can attach up to 3 files."); if (e.target) e.target.value = ""; return; }
    { const chk = validateUpload(f, "resource"); if (!chk.ok) { toast.error(chk.message); if (e.target) e.target.value = ""; return; } }
    setUploading(true);
    try { const url = await uploadFile(f, "resource"); setO((s) => ({ ...s, files: [...(s.files || []), { url, name: f.name }] })); toast.success("File attached"); }
    catch (_) { toast.error("Upload failed"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const setFileName = (i, name) => set("files", (o.files || []).map((f, j) => (j === i ? { ...f, name } : f)));

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/dashboard/resources" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">{o.title || "Resource"}</h1>
        <div className="flex items-center gap-3">
          {o.published && o.slug && <Link to={`/resource/${o.slug}`} target="_blank"><Button variant="secondary"><Eye className="mr-2 h-4 w-4" /> View public</Button></Link>}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <Switch checked={!!o.published} onCheckedChange={(v) => save({ published: v })} data-testid="resource-publish-toggle" />
            <span className="text-sm font-[540]">{o.published ? "Published" : "Hidden"}</span>
          </div>
          <Button onClick={() => save()} disabled={saving} data-testid="resource-save-button-top">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-[640]">Cover</h2>
          <div className="mt-4"><CoverUploader value={o.cover_url} onChange={(u) => set("cover_url", u)} testId="resource-cover" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Details</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Title *</Label><Input value={o.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="resource-title" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Custom URL slug</Label><div className="flex items-center rounded-lg border border-input bg-card px-3"><span className="text-sm text-muted-foreground">startupotter.com/resource/</span><input className="h-10 w-full bg-transparent px-1 text-sm outline-none" value={o.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="resource-slug" /></div></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Summary ({(o.summary || "").length}/70)</Label><Input maxLength={70} value={o.summary || ""} onChange={(e) => set("summary", e.target.value)} placeholder="One-line description" data-testid="resource-summary" /></div>
            <div className="space-y-1.5"><Label>Type</Label><RefSelect list={references.resource_types} value={o.resource_type} onChange={(v) => set("resource_type", v)} placeholder="Select type" testId="resource-type" /></div>
            {isAdmin && (
            <div className="space-y-1.5"><Label>Listing type</Label>
              <Select value={o.source || "community"} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="resource-source"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="community">Community</SelectItem><SelectItem value="internal">Internal / Official</SelectItem></SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-1.5"><Label>Author / creator</Label><Input value={o.author_name || ""} onChange={(e) => set("author_name", e.target.value)} placeholder="e.g. Rob Fitzpatrick" data-testid="resource-author" /></div>
            <div className="space-y-1.5"><Label>External link</Label><Input value={o.external_url || ""} onChange={(e) => set("external_url", e.target.value)} placeholder="https://…" data-testid="resource-external-url" /></div>
            <div className="space-y-1.5"><Label>Contributed by</Label>
              <Select value={o.organizer_type || "personal"} onValueChange={(v) => { set("organizer_type", v); if (v === "personal") set("organizer_id", null); }}>
                <SelectTrigger data-testid="resource-organizer-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="personal">Myself (personal)</SelectItem><SelectItem value="business">A business I run</SelectItem></SelectContent>
              </Select>
            </div>
            {o.organizer_type === "business" && (
              <div className="space-y-1.5 sm:col-span-2"><Label>Which business</Label><RefSelect list={businesses.map((b) => ({ value: b.id, label: b.display_name }))} value={o.organizer_id} onChange={(v) => set("organizer_id", v)} placeholder="Select a business" testId="resource-organizer-business" /></div>
            )}
          </div>
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <Label>Description</Label>
              <span className={`text-xs tabular-nums ${PLAIN(o.description) > 1500 ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="resource-description-count">{PLAIN(o.description).toLocaleString()}/1,500</span>
            </div>
            <RichTextEditor value={o.description} onChange={(v) => set("description", v)} toolbar={FULL_TOOLBAR} placeholder="Describe this resource…" testId="resource-description" />
          </div>
          <div className="mt-4 space-y-1.5"><Label>Topics</Label><MultiSelect options={opts(references.resource_topics)} value={o.topics || []} onChange={(v) => set("topics", v)} placeholder="Select topics" testId="resource-topics" /></div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Industries</Label><MultiSelect options={opts(references.industries)} value={o.industries || []} onChange={(v) => set("industries", v)} placeholder="Select industries" testId="resource-industries" /></div>
            <div className="space-y-1.5"><Label>Business classifications</Label><MultiSelect options={opts(references.business_classifications)} value={o.business_classifications || []} onChange={(v) => set("business_classifications", v)} placeholder="Select classifications" testId="resource-classifications" /></div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Location</h2>
          <p className="mt-1 text-sm text-muted-foreground">Optional. Add a location so this resource can be found by distance in search.</p>
          <div className="mt-4">
            <LocationFields value={o.location || {}} onChange={(l) => set("location", { ...(o.location || {}), ...l })} testIdPrefix="resource-location" />
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640]">Downloadable files</h2>
          <p className="mt-1 text-sm text-muted-foreground">Attach up to 3 files (PDFs, templates, decks) up to 50 MB each, and give each a clear name. They&apos;ll appear for download on the resource page.</p>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
          <div className="mt-3 space-y-2">
            {(o.files || []).map((f, i) => (
              <div key={f.url || `${f.name || "file"}-${i}`} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input value={f.name || ""} onChange={(e) => setFileName(i, e.target.value)} placeholder="File name / title" className="h-8 flex-1 border-0 bg-transparent px-1 focus-visible:ring-0" data-testid={`resource-file-name-${i}`} />
                <button onClick={() => set("files", (o.files || []).filter((_, j) => j !== i))} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label="Remove file" data-testid={`resource-file-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            {(o.files || []).length < 3 ? (
              <Button type="button" variant="secondary" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} data-testid="resource-file-upload">
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />} Attach file
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">Maximum of 3 files reached.</p>
            )}
          </div>
        </Card>

        <Card className="p-6" data-testid="resource-groups-card">
          <h2 className="mb-4 text-base font-[640]">Groups &amp; visibility</h2>
          <GroupAssociationFields
            contentType="resource" testId="resource-groups"
            value={groupIds} onChange={setGroupIds}
            visibility={visibility} onVisibilityChange={setVisibility}
            links={o.groups || []} published={Boolean(o.published)} />
        </Card>

        <Separator />
        <div className="flex justify-end"><Button size="lg" onClick={() => save()} disabled={saving} data-testid="resource-save-button">{saving ? "Saving…" : "Save"}</Button></div>
      </div>
    </div>
  );
}
